import { useEffect, useRef, useState } from "react";
import { isFunction } from "lodash";
import type { CreateSubmissionPayload } from "@/react/api/submissionsApi";
import { ThankYouPage } from "@/react/components/FormRenderer/ThankYouPage";
import { useSubmitForm } from "@/react/components/FormRenderer/hooks/useSubmitForm";
import { useTurnstile } from "@/react/components/OnboardingPage/hooks/useTurnstile";
import { usePunditUserContext } from "@/react/contexts";
import type { ElementType } from "@/react/types/Element";
import { TurnstileWrapper } from "@circle-react/components/OnboardingPage/CreateCommunityPage/TurnstileWrapper";
import { classNames } from "@circle-react/helpers/twMergeWithCN";
import type { Form as FormType } from "@circle-react/types/Form";
import { Form } from "@circle-react-uikit/Form";
import { HorizontalElementsRenderer } from "./ElementsRenderer/HorizontalElementsRenderer";
import { VerticalElementsRenderer } from "./ElementsRenderer/VerticalElementsRenderer";

interface FormContentProps {
  backgroundColor?: string;
  form: FormType;
  formElements: ElementType[];
  index: number;
  initialFocusRef?: React.RefObject<HTMLInputElement>;
  isModal: boolean;
  isPreview: boolean;
  isUsedOnPages?: boolean;
  setShouldHideTitle?: (value: boolean) => void;
  textColor?: string;
}

export const FormContent = ({
  form,
  index,
  formElements,
  isPreview,
  isModal,
  isUsedOnPages = false,
  initialFocusRef,
  setShouldHideTitle,
  textColor,
  backgroundColor,
}: FormContentProps) => {
  const [formHeight, setFormHeight] = useState(0);
  const [errors, setErrors] = useState({});
  const formRef = useRef<HTMLInputElement>(null);
  const { currentCommunitySettings } = usePunditUserContext();
  const hasTurnstile = Boolean(
    currentCommunitySettings?.forms_turnstile_enabled,
  );
  const isFormsTurnstileDisabled = !hasTurnstile || isPreview;
  const { turnstileRef, dispatch, canUseTurnstile, shouldShowTurnstile } =
    useTurnstile(isFormsTurnstileDisabled);

  // When isModal, we use a Modal.Title to render the first title, so we need to filter it out here.
  const filteredFormElements = isModal
    ? (() => {
        const firstTitleIndex = formElements.findIndex(
          element =>
            element.type === "text" && element.attributes.text_type === "title",
        );
        return formElements.filter((_, index) => index !== firstTitleIndex);
      })()
    : formElements;

  const { mutate, isSubmitting, shouldRenderThankYouPage } = useSubmitForm({
    form,
    index,
    setErrors,
    isModal,
  });

  const handleSubmit = (payload: CreateSubmissionPayload) => {
    mutate({
      turnstile_token: turnstileRef.current?.getResponse(),
      ...payload,
    });
  };
  useEffect(() => {
    setFormHeight(formRef.current?.scrollHeight || 0);
  }, []);

  useEffect(() => {
    if (isFunction(setShouldHideTitle)) {
      setShouldHideTitle(shouldRenderThankYouPage);
    }
  }, [shouldRenderThankYouPage, setShouldHideTitle]);

  const ElementsRenderer =
    form.orientation === "vertical"
      ? VerticalElementsRenderer
      : HorizontalElementsRenderer;

  if (shouldRenderThankYouPage) {
    return (
      <ThankYouPage
        uid={form.uid}
        title={form.thank_you_page_title}
        body={form.thank_you_page_body}
        backgroundColor={backgroundColor}
        textColor={textColor}
        isPreview={isPreview}
        formHeight={formHeight}
        isModal={isModal}
        isUsedOnPages={isUsedOnPages}
      />
    );
  }
  return (
    <div ref={formRef}>
      <Form
        id={`form-${form.uid}`}
        onSubmit={isPreview ? () => {} : handleSubmit}
        className={classNames("w-full px-0.5", {
          "max-w-[600px]": !isModal && !isUsedOnPages,
          "py-12": !isModal && isPreview && !isUsedOnPages,
          "pb-6 pt-0": isModal && !isUsedOnPages,
        })}
        formClassName="mb-0"
        style={{
          backgroundColor: !isUsedOnPages ? backgroundColor : undefined,
        }}
      >
        {!isModal && (
          <style>{`#form-${form.uid} label > span, #form-${form.uid} h4, #form-${form.uid} h5 { color: ${textColor}; }`}</style>
        )}
        <ElementsRenderer
          formElements={filteredFormElements}
          isPreview={isPreview}
          isSubmitting={isSubmitting}
          initialFocusRef={initialFocusRef}
          errors={errors}
        />
        {canUseTurnstile && (
          <div
            className={classNames("mt-6 flex w-full justify-center", {
              hidden: !shouldShowTurnstile,
            })}
          >
            <TurnstileWrapper
              turnstileRef={turnstileRef}
              dispatch={dispatch}
              shouldShowTurnstile={shouldShowTurnstile}
            />
          </div>
        )}
      </Form>
    </div>
  );
};
